import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useBranding } from "@/context/BrandingContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { SettingsShell } from "./SettingsShell";
import { useAuth } from "@/context/AuthContext";

export default function BrandingSettings() {
    const { user } = useAuth();
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);
    const { reloadOrg } = useBranding();

    useEffect(() => {
        api.get("/branding/organization").then(({ data }) => setForm(data)).catch(() => {});
    }, []);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch("/branding/organization", {
                company_name: form.company_name,
                tagline: form.tagline,
                logo_url: form.logo_url,
                primary_color: form.primary_color,
                contact_email: form.contact_email,
                contact_phone: form.contact_phone,
                address: form.address,
                domain: form.domain,
                report_header: form.report_header,
            });
            await reloadOrg();
            toast.success("Branding updated");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const extraTabs = user?.is_super_admin ? [{ to: "/app/settings/software-branding", label: "Software Branding" }] : [];

    return (
        <SettingsShell title="Organization branding" subtitle="Customize how your organization appears across reports, portals, and the app header." extraTabs={extraTabs}>
            <form onSubmit={save} className="max-w-3xl space-y-6" data-testid="branding-form">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label>Company name</Label>
                        <Input value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-2" data-testid="branding-company-name" />
                    </div>
                    <div>
                        <Label>Primary color</Label>
                        <div className="mt-2 flex items-center gap-2">
                            <Input type="color" value={form.primary_color || "#1E293B"} onChange={(e) => set("primary_color", e.target.value)} className="w-16 h-10 p-1" data-testid="branding-color-picker" />
                            <Input value={form.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} className="flex-1" data-testid="branding-color-hex" />
                        </div>
                    </div>
                </div>
                <div>
                    <Label>Tagline</Label>
                    <Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className="mt-2" data-testid="branding-tagline" />
                </div>
                <div>
                    <Label>Logo URL</Label>
                    <Input value={form.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://…/logo.png" className="mt-2" data-testid="branding-logo-url" />
                    <div className="text-xs text-muted-foreground mt-1">Direct file upload arrives in Release 2 (object storage). Use a hosted URL for now.</div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <Label>Contact email</Label>
                        <Input type="email" value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-2" data-testid="branding-contact-email" />
                    </div>
                    <div>
                        <Label>Contact phone</Label>
                        <Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="mt-2" data-testid="branding-contact-phone" />
                    </div>
                </div>
                <div>
                    <Label>Address</Label>
                    <Textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-2" rows={2} data-testid="branding-address" />
                </div>
                <div>
                    <Label>Custom domain</Label>
                    <Input value={form.domain || ""} onChange={(e) => set("domain", e.target.value)} placeholder="my-portal.example.com (Enterprise)" className="mt-2" disabled data-testid="branding-domain" />
                    <div className="text-xs text-muted-foreground mt-1">Custom domains available on the Enterprise plan (Release 2).</div>
                </div>
                <div>
                    <Label>Report header</Label>
                    <Textarea value={form.report_header || ""} onChange={(e) => set("report_header", e.target.value)} className="mt-2" rows={2} data-testid="branding-report-header" />
                </div>

                <div className="border-t border-border pt-6">
                    <button type="submit" disabled={saving} className="bg-foreground text-background px-6 py-3 text-sm hover:opacity-90 disabled:opacity-50" data-testid="branding-save">
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </form>
        </SettingsShell>
    );
}
